import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { tenantAPI, propertyAPI, leasesAPI } from '../services/api';

const Leases = () => {
  const [leases, setLeases] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [properties, setProperties] = useState([]);
  const [uploadingId, setUploadingId] = useState('');
  const [fileMap, setFileMap] = useState({});
  
  // New upload section state
  const [selectedTenant, setSelectedTenant] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    const [ls, ts, ps] = await Promise.all([leasesAPI.getAll(), tenantAPI.getAll(), propertyAPI.getAll()]);
    setLeases(ls || []);
    setTenants(ts || []);
    setProperties(ps || []);
  };

  useEffect(() => { load(); }, []);

  const tMap = useMemo(() => Object.fromEntries((tenants||[]).map(t => [t.id || t._id, t])), [tenants]);
  const pMap = useMemo(() => Object.fromEntries((properties||[]).map(p => [p.id || p._id, p])), [properties]);

  // New upload handler for the upload section
  const handleNewUpload = async () => {
    if (!selectedTenant || !selectedFile) {
      alert('Please select a tenant and choose a file to upload');
      return;
    }
    
    setUploading(true);
    try {
      // First create a lease record for this tenant
      const tenant = tenants.find(t => (t.id || t._id) === selectedTenant);
      if (!tenant) {
        alert('Selected tenant not found');
        return;
      }
      
      const newLease = await leasesAPI.create({
        tenant_id: selectedTenant,
        property_id: tenant.property_id,
        status: 'active',
        start_date: new Date().toISOString(),
      });
      
      // Then upload the document to this lease
      const leaseId = newLease.id || newLease._id;
      await leasesAPI.uploadDoc(leaseId, selectedFile);
      
      // Reset form and reload
      setSelectedTenant('');
      setSelectedFile(null);
      await load();
      alert('Lease document uploaded successfully!');
    } catch (error) {
      console.error('Error uploading lease:', error);
      alert('Failed to upload lease document. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const uploadDoc = async (leaseId) => {
    const file = fileMap[leaseId];
    if (!file) return;
    setUploadingId(leaseId);
    try {
      await leasesAPI.uploadDoc(leaseId, file);
      await load();
    } finally {
      setUploadingId('');
      setFileMap(prev => ({ ...prev, [leaseId]: null }));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Leases</h1>
          <p className="text-gray-600 mt-1">Upload and manage lease documents</p>
        </div>
      </div>

      {/* Upload Section - Always Visible */}
      <Card>
        <CardHeader>
          <CardTitle>Upload Lease Document</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Tenant
              </label>
              <select 
                className="w-full border rounded-md p-2"
                value={selectedTenant}
                onChange={(e) => setSelectedTenant(e.target.value)}
                disabled={uploading}
              >
                <option value="">Choose tenant...</option>
                {tenants.map(t => (
                  <option key={t.id || t._id} value={t.id || t._id}>
                    {t.name} - {properties.find(p => (p.id || p._id) === t.property_id)?.name || 'N/A'}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Choose File
              </label>
              <input
                type="file"
                accept=".pdf,.doc,.docx"
                className="w-full border rounded-md p-2"
                onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                disabled={uploading}
              />
            </div>
            <div>
              <Button 
                className="w-full bg-blue-600 hover:bg-blue-700"
                onClick={handleNewUpload}
                disabled={uploading || !selectedTenant || !selectedFile}
              >
                {uploading ? 'Uploading...' : 'Upload Lease'}
              </Button>
            </div>
          </div>
          <p className="text-sm text-gray-500 mt-3">
            Accepted formats: PDF, DOC, DOCX
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>All Leases</CardTitle></CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tenant</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Property</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Start</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">End</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Documents</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {leases.map(l => {
                  const t = tMap[l.id] || tMap[l._id] || tMap[l.tenant_id];
                  const p = pMap[l.id] || pMap[l._id] || pMap[l.property_id];
                  const docs = Array.isArray(l.documents) ? l.documents : [];
                  return (
                    <tr key={l.id || l._id}>
                      <td className="px-6 py-4 text-sm">{t?.name || '—'}</td>
                      <td className="px-6 py-4 text-sm">{p?.name || '—'}</td>
                      <td className="px-6 py-4 text-sm">{l.status}</td>
                      <td className="px-6 py-4 text-sm">{l.start_date ? new Date(l.start_date).toLocaleDateString() : '—'}</td>
                      <td className="px-6 py-4 text-sm">{l.end_date ? new Date(l.end_date).toLocaleDateString() : '—'}</td>
                      <td className="px-6 py-4 text-sm">
                        {docs.length > 0 ? (
                          <div className="flex flex-col gap-1">
                            {docs.map((doc, idx) => (
                              <span key={idx} className="text-blue-600">
                                📄 {doc.filename || `Document ${idx + 1}`}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-gray-400">No documents</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-right text-sm">
                        <div className="flex gap-2 justify-end items-center">
                          {docs.length > 0 ? (
                            docs.map((doc, idx) => (
                              <Button 
                                key={idx}
                                size="sm" 
                                variant="outline"
                                onClick={() => window.open(doc.url || doc.path, '_blank')}
                              >
                                View/Download
                              </Button>
                            ))
                          ) : (
                            <span className="text-gray-400 text-xs">Upload document above</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Leases;